<template>
  <div class="h-full flex flex-col bg-[#f8fafc] gap-6 overflow-hidden">
    <div class="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169] flex flex-col">
      <div class="px-6 pt-5 pb-1 flex justify-between items-center shrink-0">
        <h2 class="text-base font-bold text-[#0d4169] text-left">Debone Cost</h2>
      </div>

      <div class="px-4 sm:px-6 pb-5 pt-2 shrink-0">
        <div class="flex flex-wrap gap-x-3 sm:gap-x-10 gap-y-3 w-full">
          <div class="flex flex-col gap-1 w-full min-w-0 sm:w-[200px]">
            <label class="text-[0.7rem] font-bold text-slate-600 text-left">Department</label>
            <select
              v-model="filters.departmentId"
              class="w-full px-2 py-1.5 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs bg-white cursor-pointer h-[34px]"
              @change="handleDepartmentChange"
            >
              <option value="">Select Department</option>
              <option v-for="dep in activeDepartments" :key="dep.id" :value="String(dep.id)">
                {{ dep.name }}
              </option>
            </select>
          </div>

          <div class="flex flex-col gap-1 w-full min-w-0 sm:w-[260px]">
            <label class="text-[0.7rem] font-bold text-slate-600 text-left flex items-center justify-between gap-2">
              <span>Select Item</span>
              <span v-if="filters.itemId" class="text-[0.68rem] text-[#0d4169] font-semibold whitespace-nowrap">
                Recent Price: {{ formatPrice(selectedRecentPrice) }}
              </span>
            </label>
            <select
              v-model="filters.itemId"
              class="w-full px-2 py-1.5 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs bg-white cursor-pointer h-[34px]"
              @change="handleItemChange"
            >
              <option value="">Select Item</option>
              <option v-for="item in selectableItems" :key="item.id" :value="String(item.id)">
                {{ item.itemName }}
              </option>
            </select>
          </div>

          <div
            v-if="isSelectedItemPieces"
            class="flex flex-col gap-1 w-full min-w-0 sm:w-[200px]"
          >
            <label class="text-[0.7rem] font-bold text-slate-600 text-left">How many Pieces</label>
            <input
              v-model.number="howManyPieces"
              type="number"
              min="0"
              class="w-full px-2 py-1.5 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs bg-white h-[34px]"
              placeholder="0"
            >
          </div>
        </div>
      </div>

      <div class="px-6 pb-6 overflow-auto flex-1 custom-scrollbar space-y-8">
        <div v-if="bootstrapLoading" class="flex items-center justify-center py-16">
          <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
        </div>

        <template v-else>
          <!-- Debone Cost Table -->
          <div>
            <!-- <h3 class="text-sm font-bold text-[#0d4169] text-left mb-3">Debone Cost</h3> -->
            <div v-if="tablesLoading" class="flex items-center justify-center py-10">
              <div class="h-7 w-7 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <template v-else>
              <div class="w-full overflow-x-auto">
                <table class="w-full min-w-[720px] debone-cost-table">
                  <thead>
                    <tr>
                      <th class="text-center">No.</th>
                      <th class="text-center">Debone Name</th>
                      <th class="text-center">UOM</th>
                      <th class="text-center">Percentage</th>
                      <th class="text-center">Qty PC</th>
                      <th class="text-center">Qty KG/L</th>
                      <th class="text-center">U.P</th>
                      <th class="text-center">T.P</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, index) in registrationRows"
                      :key="row.id"
                      class="hover:bg-slate-50 transition-colors"
                    >
                      <td class="text-center">{{ index + 1 }}</td>
                      <td class="text-center">{{ row.deboniName || row.productName || '-' }}</td>
                      <td class="text-center text-slate-600">{{ row.uom || '-' }}</td>
                      <td class="text-center font-bold text-blue-600">
                        {{ row.percentage != null ? `${row.percentage}%` : '-' }}
                      </td>
                      <td class="text-center">
                        <template v-if="isQtyPcEditable(row)">
                          <input
                            v-model.number="row.qtyPc"
                            type="number"
                            min="0"
                            class="w-16 mx-auto px-1 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm text-center bg-red-50 focus:bg-white"
                          >
                        </template>
                        <span v-else-if="isSelectedItemPieces && !hasHowManyPieces" class="text-slate-400">-</span>
                        <span v-else class="text-slate-700">{{ formatQty(getRowQtyPc(row)) }}</span>
                      </td>
                      <td class="text-center">
                        <template v-if="qtyFieldsEditable">
                          <input
                            v-model.number="row.qtyKgL"
                            type="number"
                            min="0"
                            step="0.01"
                            :class="[
                              'w-16 mx-auto px-1 py-0.5 border rounded outline-none transition-all text-sm text-center focus:bg-white',
                              isQtyKgLMissing(row)
                                ? 'border-red-400 bg-red-50 focus:border-red-500'
                                : 'border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] bg-red-50',
                            ]"
                          >
                        </template>
                        <span v-else class="text-slate-400">-</span>
                      </td>
                      <td class="text-center text-slate-700">{{ formatPrice(getRowUp(row)) }}</td>
                      <td class="text-center text-slate-700">{{ formatPrice(getRowTp(row)) }}</td>
                    </tr>
                    <tr v-if="!canFetchTables">
                      <td colspan="8" class="text-center py-8 text-slate-400 italic">
                        Select a department and item to load Debone Cost rows.
                      </td>
                    </tr>
                    <tr v-else-if="registrationRows.length === 0">
                      <td colspan="8" class="text-center py-8 text-slate-400 italic">
                        No Debone Cost rows found for this department and item.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p v-if="saveError" class="mt-3 text-center text-xs text-red-600 font-semibold">
                {{ saveError }}
              </p>

              <div class="flex justify-center mt-6">
                <button
                  type="button"
                  class="px-6 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md hover:bg-[#0a3354] transition-all active:scale-95 shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
                  :disabled="!canSave || saving || !canAddDeboneCost"
                  @click="handleSave"
                >
                  {{ saving ? 'Saving...' : 'Save' }}
                </button>
              </div>
            </template>
          </div>

          <!-- Table 3: Cost Report -->
          <div>
            <h3 class="text-sm font-bold text-[#0d4169] text-left mb-3">Cost Report</h3>
            <div class="flex flex-wrap gap-x-3 sm:gap-x-10 gap-y-3 w-full mb-3">
              <div class="flex flex-col gap-1 w-full min-w-0 sm:w-[200px]">
                <label class="text-[0.7rem] font-bold text-slate-600 text-left">Date</label>
                <input
                  v-model="savedCostDate"
                  type="date"
                  required
                  class="w-full px-2 py-1.5 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs bg-white h-[34px]"
                  @change="loadSavedCost"
                >
              </div>

              <div class="flex flex-col gap-1 w-full min-w-0 sm:w-[200px]">
                <label class="text-[0.7rem] font-bold text-slate-600 text-left">Department</label>
                <select
                  v-model="savedCostFilters.departmentId"
                  class="w-full px-2 py-1.5 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs bg-white cursor-pointer h-[34px]"
                  @change="handleSavedCostDepartmentChange"
                >
                  <option value="">All Departments</option>
                  <option v-for="dep in activeDepartments" :key="dep.id" :value="String(dep.id)">
                    {{ dep.name }}
                  </option>
                </select>
              </div>

              <div class="flex flex-col gap-1 w-full min-w-0 sm:w-[200px]">
                <label class="text-[0.7rem] font-bold text-slate-600 text-left">Select Item</label>
                <select
                  v-model="savedCostFilters.itemId"
                  class="w-full px-2 py-1.5 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs bg-white cursor-pointer h-[34px]"
                >
                  <option value="">All Items</option>
                  <option v-for="item in savedCostSelectableItems" :key="item.id" :value="String(item.id)">
                    {{ item.itemName }}
                  </option>
                </select>
              </div>
            </div>
            <div v-if="savedCostLoading" class="flex items-center justify-center py-10">
              <div class="h-7 w-7 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <template v-else>
              <div v-if="groupedSavedCostRows.length > 0" class="w-full space-y-4">
                <div
                  v-for="group in groupedSavedCostRows"
                  :key="group.groupingId"
                  class="w-full overflow-x-auto rounded-xl border border-slate-200 border-t-4 border-t-[#0d4169] bg-white"
                >
                  <p
                    v-if="formatPiecesUsedLabel(group.piecesUsed, group.itemName)"
                    class="px-3 pt-2 pb-1 text-xs font-semibold text-[#0d4169] text-center"
                  >
                    {{ formatPiecesUsedLabel(group.piecesUsed, group.itemName) }}
                  </p>
                  <table class="w-full min-w-[720px] debone-cost-table">
                    <thead>
                      <tr>
                        <th class="text-center">No.</th>
                        <th class="text-center">Debone Name</th>
                        <th class="text-center">UOM</th>
                        <th class="text-center">Percentage</th>
                        <th class="text-center">Qty PC</th>
                        <th class="text-center">Qty KG/L</th>
                        <th class="text-center">U.P</th>
                        <th class="text-center">T.P</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(row, index) in group.rows"
                        :key="row.id"
                        class="hover:bg-slate-50 transition-colors"
                      >
                        <td class="text-center">{{ index + 1 }}</td>
                        <td class="text-center">{{ row.deboniName || '-' }}</td>
                        <td class="text-center text-slate-600">{{ row.uom || '-' }}</td>
                        <td class="text-center font-bold text-blue-600">
                          {{ row.percentage != null ? `${row.percentage}%` : '-' }}
                        </td>
                        <td class="text-center">
                          <template v-if="isSavedQtyPcEditable(row)">
                            <input
                              v-model.number="row.qtyPc"
                              type="number"
                              min="0"
                              class="w-16 mx-auto px-1 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm text-center bg-red-50 focus:bg-white"
                            >
                          </template>
                          <span v-else class="text-slate-700">{{ formatQty(row.qtyPc) }}</span>
                        </td>
                        <td class="text-center">
                          <input
                            v-model.number="row.qtyKgL"
                            type="number"
                            min="0"
                            step="0.01"
                            class="w-16 mx-auto px-1 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm text-center bg-red-50 focus:bg-white"
                          >
                        </td>
                        <td class="text-center text-slate-700">{{ formatPrice(getSavedRowUp(row)) }}</td>
                        <td class="text-center text-slate-700">{{ formatPrice(row.totalPrice) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else-if="savedCostDate && savedCostRows.length > 0 && filteredSavedCostRows.length === 0" class="text-center py-8 text-slate-400 italic text-xs">
                No saved cost rows found for the selected department and item.
              </div>
              <div v-else-if="savedCostDate" class="text-center py-8 text-slate-400 italic text-xs">
                No saved cost rows found for this date.
              </div>

              <p v-if="savedCostError" class="mt-3 text-center text-xs text-red-600 font-semibold">
                {{ savedCostError }}
              </p>

              <div v-if="savedCostRows.length > 0" class="flex justify-center mt-6">
                <button
                  type="button"
                  class="px-6 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md hover:bg-[#0a3354] transition-all active:scale-95 shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
                  :disabled="!hasSavedCostChanges || savedCostUpdating || !canEditDeboneCost"
                  @click="handleUpdateSavedCost"
                >
                  {{ savedCostUpdating ? 'Updating...' : 'Update Change' }}
                </button>
              </div>
            </template>
          </div>
        </template>
      </div>
    </div>

    <!-- Toast (page-local only) -->
    <Transition name="toast">
      <div v-if="toast.show" class="fixed top-4 right-4 z-[1100] max-w-sm pointer-events-none">
        <div
          :class="[
            'px-6 py-3 rounded-lg shadow-2xl text-white flex items-center gap-3 min-w-[280px] pointer-events-auto',
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          ]"
        >
          <span v-if="toast.type === 'success'">✅</span>
          <span v-else>❌</span>
          <span class="font-medium text-sm">{{ toast.message }}</span>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import store from '../../store'
import { apiFetch } from '../../utils/api'

const departments = ref([])
const registrations = ref([])
const registrationRows = ref([])
const selectedItemUnit = ref('')
const selectedItemRecentUnitPrice = ref(0)
const selectedItemRecentVolume = ref(0)
const howManyPieces = ref('')
const bootstrapLoading = ref(false)
const tablesLoading = ref(false)
const saving = ref(false)
const saveError = ref('')
const showQtyKgLErrors = ref(false)
const savedCostDate = ref('')
const savedCostRows = ref([])
const savedCostOriginal = ref([])
const savedCostLoading = ref(false)
const savedCostUpdating = ref(false)
const savedCostError = ref('')

const toast = ref({ show: false, message: '', type: 'success' })
let toastTimer = null

function showToast(message, type = 'success') {
  if (toastTimer) clearTimeout(toastTimer)
  toast.value = { show: true, message, type }
  toastTimer = setTimeout(() => {
    toast.value.show = false
  }, 3500)
}

const filters = ref({
  departmentId: '',
  itemId: '',
})

const savedCostFilters = ref({
  departmentId: '',
  itemId: '',
})

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)

const userPrivileges = computed(() => {
  if (!currentUser.value?.privilege) return {}
  return typeof currentUser.value.privilege === 'string'
    ? JSON.parse(currentUser.value.privilege)
    : currentUser.value.privilege
})
const hasAdminPrivilege = computed(() => userPrivileges.value?.All === true || userPrivileges.value?.all === true)
const deboneCostPrivilege = computed(() =>
  userPrivileges.value?.['Debone Cost'] || userPrivileges.value?.['debone cost'] || null
)
/** Save requires All or Debone Cost Add */
const canAddDeboneCost = computed(() => hasAdminPrivilege.value || !!deboneCostPrivilege.value?.Add)
/** Update Change requires All or Debone Cost Edit */
const canEditDeboneCost = computed(() => hasAdminPrivilege.value || !!deboneCostPrivilege.value?.Edit)

const canFetchTables = computed(() => {
  return Boolean(String(filters.value.departmentId || '').trim() && String(filters.value.itemId || '').trim())
})

function isActiveDeboneRegistration(row) {
  return (row?.status ?? 1) === 1
}

const activeDepartments = computed(() =>
  departments.value
    .filter((d) => {
      if ((d.status ?? 1) !== 1) return false
      const isCentral = d.isCentral ?? d.is_central
      return isCentral === 0 || isCentral === false
    })
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }))
)

const selectableItems = computed(() => {
  let rows = registrations.value.filter(isActiveDeboneRegistration)

  const depId = String(filters.value.departmentId || '').trim()
  if (depId) {
    rows = rows.filter((row) => String(row.departmentId ?? '') === depId)
  }

  const map = new Map()
  for (const row of rows) {
    if (row.itemId == null || row.itemId === '') continue
    const id = String(row.itemId)
    if (map.has(id)) continue
    map.set(id, {
      id: row.itemId,
      itemName: row.itemName || '-',
    })
  }

  return [...map.values()].sort((a, b) =>
    String(a.itemName || '').localeCompare(String(b.itemName || ''), undefined, { sensitivity: 'base' })
  )
})

const savedCostSelectableItems = computed(() => {
  let rows = registrations.value.filter(isActiveDeboneRegistration)

  const depId = String(savedCostFilters.value.departmentId || '').trim()
  if (depId) {
    rows = rows.filter((row) => String(row.departmentId ?? '') === depId)
  }

  const map = new Map()
  for (const row of rows) {
    if (row.itemId == null || row.itemId === '') continue
    const id = String(row.itemId)
    if (map.has(id)) continue
    map.set(id, {
      id: row.itemId,
      itemName: row.itemName || '-',
    })
  }

  return [...map.values()].sort((a, b) =>
    String(a.itemName || '').localeCompare(String(b.itemName || ''), undefined, { sensitivity: 'base' })
  )
})

const filteredSavedCostRows = computed(() => {
  let rows = savedCostRows.value

  const depId = String(savedCostFilters.value.departmentId || '').trim()
  if (depId) {
    rows = rows.filter((row) => String(row.departmentId ?? '') === depId)
  }

  const itemId = String(savedCostFilters.value.itemId || '').trim()
  if (itemId) {
    rows = rows.filter((row) => String(row.itemId ?? '') === itemId)
  }

  return rows
})

function resolveSavedCostGroupKey(row) {
  const groupingId = String(row?.groupingId || '').trim()
  if (groupingId) return groupingId
  // Legacy rows (no grouping_id): keep batches that were saved together
  const ts = row?.createdAt ? new Date(row.createdAt).toISOString().slice(0, 19) : ''
  if (ts) {
    return `legacy-${ts}-${row?.departmentId ?? ''}-${row?.itemId ?? ''}`
  }
  return `legacy-${row?.id}`
}

function getRowCreatedAtMs(row) {
  const t = row?.createdAt ? new Date(row.createdAt).getTime() : NaN
  return Number.isFinite(t) ? t : 0
}

function getSavedCostItemName(row) {
  const fromRow = String(row?.itemName || '').trim()
  if (fromRow) return fromRow
  const itemId = String(row?.itemId ?? '').trim()
  if (!itemId) return ''
  const match = registrations.value.find((r) => String(r.itemId ?? '') === itemId)
  return String(match?.itemName || '').trim()
}

/** Group Cost Report rows by save-batch grouping_id (same id = one Save click). */
const groupedSavedCostRows = computed(() => {
  const groups = []
  const map = new Map()

  // Preserve API/list order while building each group's rows
  for (const row of filteredSavedCostRows.value) {
    const key = resolveSavedCostGroupKey(row)
    let group = map.get(key)
    if (!group) {
      group = {
        groupingId: key,
        rows: [],
        piecesUsed: 0,
        createdAtMs: 0,
        itemName: getSavedCostItemName(row),
      }
      map.set(key, group)
      groups.push(group)
    }
    group.rows.push(row)
    group.piecesUsed += Number(row.howManyPiecesUsed || 0)
    const rowMs = getRowCreatedAtMs(row)
    if (rowMs > group.createdAtMs) group.createdAtMs = rowMs
    if (!group.itemName) group.itemName = getSavedCostItemName(row)
  }

  // Newest save group first; row order inside each group stays as-is
  groups.sort((a, b) => b.createdAtMs - a.createdAtMs)
  return groups
})

function formatPiecesUsedLabel(amount, itemName) {
  const n = Number(amount || 0)
  const name = String(itemName || '').trim()
  if (!(Number.isFinite(n) && n > 0)) return name
  const display = Number.isInteger(n) ? String(n) : String(n)
  return name ? `${display} Pieces of ${name}` : `${display} Pieces`
}

function normalizeUnit(u) {
  return String(u || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
}

function isPiecesUom(u) {
  const unit = normalizeUnit(u)
  return unit === 'pieces' || unit === 'piece' || unit === 'pc' || unit === 'pcs'
}

function isLiterOrKgUom(u) {
  const unit = normalizeUnit(u)
  return (
    unit === 'liter' ||
    unit === 'litre' ||
    unit === 'liters' ||
    unit === 'l' ||
    unit === 'kg' ||
    unit === 'k.g.' ||
    unit === 'k.g' ||
    unit === 'kilogram'
  )
}

function getDerivedItemRecentPrice(unit, recentUnitPrice, recentVolume) {
  const price = Number(recentUnitPrice || 0)
  if (!isLiterOrKgUom(unit)) return price
  const volume = Number(recentVolume || 0)
  if (!(volume > 0)) return 0
  return (price / volume) * 1000
}

/** Selected Item UOM (filter) */
const resolvedSelectedItemUnit = computed(() => {
  return selectedItemUnit.value
})

const isSelectedItemKgOrLiter = computed(() => isLiterOrKgUom(resolvedSelectedItemUnit.value))
const isSelectedItemPieces = computed(() => isPiecesUom(resolvedSelectedItemUnit.value))
/** How many Pieces filled with a positive value */
const hasHowManyPieces = computed(() => {
  const n = Number(howManyPieces.value)
  return Number.isFinite(n) && n > 0
})
/**
 * Qty PC / Qty KG/L editable when:
 * - Selected item is K.G./liter, or
 * - Selected item is Pieces AND How many Pieces is filled (> 0)
 * (Pieces with empty/0 How many Pieces => both qty fields locked)
 */
const qtyFieldsEditable = computed(() => {
  if (isSelectedItemKgOrLiter.value) return true
  if (isSelectedItemPieces.value) return hasHowManyPieces.value
  return false
})

const selectedRecentPrice = computed(() =>
  getDerivedItemRecentPrice(
    resolvedSelectedItemUnit.value,
    selectedItemRecentUnitPrice.value,
    selectedItemRecentVolume.value
  )
)

const selectedQtySum = computed(() => {
  if (isSelectedItemKgOrLiter.value) {
    return registrationRows.value.reduce((sum, row) => sum + Number(row.qtyKgL || 0), 0)
  }
  // Pieces: base qty comes from "How many Pieces", not row Qty PC / registration quantity
  if (isSelectedItemPieces.value) {
    const n = Number(howManyPieces.value)
    return Number.isFinite(n) ? n : 0
  }
  return registrationRows.value.reduce((sum, row) => sum + Number(getRowQtyPc(row) || 0), 0)
})

/** Total Price base = Recent Price × (How many Pieces | Qty KG/L sum) */
const baseTotalPrice = computed(() => selectedRecentPrice.value * selectedQtySum.value)

/**
 * Qty PC editable (same rule as K.G./liter once qty fields unlock):
 * only when qty fields are editable AND Debone Name UOM is not K.G./liter.
 */
function isQtyPcEditable(row) {
  if (!qtyFieldsEditable.value) return false
  return !isLiterOrKgUom(row?.uom)
}

const percentageSum = computed(() =>
  registrationRows.value.reduce((sum, row) => sum + Number(row.percentage || 0), 0)
)

const canSave = computed(() => registrationRows.value.length > 0 && Math.abs(percentageSum.value - 100) < 0.001)

function getRowQtyPc(row) {
  // Pieces selected item: use editable qtyPc only — never debone_registration.quantity
  if (isSelectedItemPieces.value) {
    const n = Number(row.qtyPc)
    return Number.isFinite(n) ? n : 0
  }
  if (!qtyFieldsEditable.value) {
    return Number(row.quantity || 0)
  }
  const n = Number(row.qtyPc)
  return Number.isFinite(n) ? n : 0
}

/** T.P = (Recent Price × base qty) × (percentage / 100)
 * Pieces: base qty = How many Pieces (T.P calculates as soon as How many Pieces is entered)
 * K.G./liter: base qty = Qty KG/L sum (row needs Qty KG/L)
 * U.P still by Debone Name UOM (unchanged)
 */
function getRowTp(row) {
  if (isSelectedItemKgOrLiter.value) {
    const qtyKgL = Number(row.qtyKgL || 0)
    if (!(qtyKgL > 0)) return 0
  } else if (isSelectedItemPieces.value) {
    const pieces = Number(howManyPieces.value)
    if (!(Number.isFinite(pieces) && pieces > 0)) return 0
  } else {
    const qtyPc = Number(getRowQtyPc(row) || 0)
    if (!(qtyPc > 0)) return 0
  }
  const pct = Number(row.percentage || 0)
  return baseTotalPrice.value * (pct / 100)
}

/**
 * U.P by Debone Name UOM (same for Pieces item and K.G./liter item):
 * Pieces => T.P / Qty PC ; K.G./liter => T.P / Qty KG/L
 */
function getRowUp(row) {
  const tp = getRowTp(row)
  if (isPiecesUom(row.uom)) {
    const qtyPc = Number(getRowQtyPc(row) || 0)
    return qtyPc > 0 ? tp / qtyPc : 0
  }
  if (isLiterOrKgUom(row.uom)) {
    const qtyKgL = Number(row.qtyKgL || 0)
    return qtyKgL > 0 ? tp / qtyKgL : 0
  }
  return 0
}

function isQtyKgLMissing(row) {
  if (!qtyFieldsEditable.value || !showQtyKgLErrors.value) return false
  const n = Number(row.qtyKgL)
  return !(Number.isFinite(n) && n > 0)
}

/** Saved Cost: Qty PC editable only when Debone Name UOM is NOT KG/Liter */
function isSavedQtyPcEditable(row) {
  return !isLiterOrKgUom(row?.uom)
}

/**
 * Saved Cost U.P — calculated from T.P (same logic as Debone Cost table):
 * Pieces UOM => T.P / Qty PC ; KG/Liter UOM => T.P / Qty KG/L
 */
function getSavedRowUp(row) {
  const tp = Number(row.totalPrice || 0)
  if (isPiecesUom(row.uom)) {
    const qtyPc = Number(row.qtyPc || 0)
    return qtyPc > 0 ? tp / qtyPc : 0
  }
  if (isLiterOrKgUom(row.uom)) {
    const qtyKgL = Number(row.qtyKgL || 0)
    return qtyKgL > 0 ? tp / qtyKgL : 0
  }
  return 0
}

function formatPrice(value) {
  return Number(value || 0).toFixed(2)
}

function formatQty(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '-'
  return Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/\.?0+$/, '')
}

function clearTables() {
  registrationRows.value = []
  selectedItemUnit.value = ''
  selectedItemRecentUnitPrice.value = 0
  selectedItemRecentVolume.value = 0
  howManyPieces.value = ''
  saveError.value = ''
  showQtyKgLErrors.value = false
}

function resetForm() {
  filters.value.departmentId = ''
  filters.value.itemId = ''
  clearTables()
}

function handleDepartmentChange() {
  // Always reset both tables on department reselect
  clearTables()
  const allowed = new Set(selectableItems.value.map((item) => String(item.id)))
  if (filters.value.itemId && !allowed.has(String(filters.value.itemId))) {
    filters.value.itemId = ''
  }
}

function handleItemChange() {
  // Always reset both tables on item reselect
  clearTables()
}

function handleSavedCostDepartmentChange() {
  const allowed = new Set(savedCostSelectableItems.value.map((item) => String(item.id)))
  if (savedCostFilters.value.itemId && !allowed.has(String(savedCostFilters.value.itemId))) {
    savedCostFilters.value.itemId = ''
  }
}

async function handleSave() {
  saveError.value = ''

  if (!canAddDeboneCost.value || !canSave.value || saving.value) return

  if (isSelectedItemPieces.value && !hasHowManyPieces.value) {
    saveError.value = 'How many Pieces is required when the selected item UOM is Pieces.'
    showToast(saveError.value, 'error')
    return
  }

  // Same Qty KG/L requirement for K.G./liter and unlocked Pieces (How many Pieces filled)
  if (qtyFieldsEditable.value) {
    const missing = registrationRows.value.some((row) => {
      const n = Number(row.qtyKgL)
      return !(Number.isFinite(n) && n > 0)
    })
    if (missing) {
      showQtyKgLErrors.value = true
      saveError.value = 'Qty KG/L is required for all Debone Cost rows.'
      showToast(saveError.value, 'error')
      return
    }
  }
  showQtyKgLErrors.value = false

  const piecesUsedTotal = isSelectedItemPieces.value ? Number(howManyPieces.value || 0) : 0

  const payload = {
    departmentId: Number(filters.value.departmentId),
    itemId: Number(filters.value.itemId),
    howManyPieces: piecesUsedTotal,
    sivIds: [],
    nsIds: [],
    rows: registrationRows.value.map((row, index) => ({
      deboneId: row.id,
      percentage: Number(row.percentage || 0),
      qtyPc: getRowQtyPc(row),
      qtyKgL: qtyFieldsEditable.value ? Number(row.qtyKgL || 0) : null,
      unitPrice: getRowUp(row),
      totalPrice: getRowTp(row),
      // Pieces only: store How many Pieces used on 1st row; remaining rows = 0
      howManyPiecesUsed: isSelectedItemPieces.value && index === 0 ? piecesUsedTotal : 0,
    })),
  }

  saving.value = true
  try {
    const response = await apiFetch('/debones/cost', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(data.message || 'Failed to save Debone Cost')
    }
    showToast(data.message || 'Debone Cost saved successfully.', 'success')
    resetForm()
  } catch (err) {
    showToast(err?.message || 'Failed to save Debone Cost', 'error')
  } finally {
    saving.value = false
  }
}

async function loadDepartments() {
  if (!companyId.value) return
  let endpoint = `/departments/company/${companyId.value}`
  if (branchId.value) {
    endpoint = `/departments/branch/${branchId.value}`
  }
  const response = await apiFetch(endpoint, { method: 'GET' })
  if (!response.ok) return
  const data = await response.json().catch(() => [])
  departments.value = Array.isArray(data) ? data : []
}

async function loadRegistrationsForDropdown() {
  const response = await apiFetch('/debones/registrations', { method: 'GET' })
  if (!response.ok) {
    registrations.value = []
    return
  }
  const data = await response.json().catch(() => [])
  registrations.value = (Array.isArray(data) ? data : []).filter(isActiveDeboneRegistration)
}

async function loadCostTables() {
  if (!canFetchTables.value) {
    clearTables()
    return
  }

  // Reset previous table state before loading the new selection
  clearTables()
  tablesLoading.value = true
  try {
    const deptId = encodeURIComponent(String(filters.value.departmentId))
    const itemId = encodeURIComponent(String(filters.value.itemId))
    const response = await apiFetch(
      `/debones/cost?departmentId=${deptId}&itemId=${itemId}`,
      { method: 'GET' }
    )
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to load Debone Cost data')
    }
    const data = await response.json().catch(() => ({}))
    selectedItemUnit.value = data.selectedItemUnit || ''
    selectedItemRecentUnitPrice.value = Number(data.selectedItemRecentUnitPrice || 0)
    selectedItemRecentVolume.value = Number(data.selectedItemRecentVolume || 0)
    registrationRows.value = (Array.isArray(data.registrations) ? data.registrations : [])
      .filter(isActiveDeboneRegistration)
      .map((row) => ({
        ...row,
        qtyPc: '',
        qtyKgL: '',
      }))
  } catch (err) {
    clearTables()
    console.error(err)
  } finally {
    tablesLoading.value = false
  }
}

watch(
  () => [filters.value.departmentId, filters.value.itemId],
  () => {
    if (canFetchTables.value) {
      loadCostTables()
    } else {
      clearTables()
    }
  }
)

watch(registrationRows, () => {
  saveError.value = ''
}, { deep: true })

const hasSavedCostChanges = computed(() => savedCostRows.value.some(hasSavedCostRowChanged))

function hasSavedCostRowChanged(row) {
  const orig = savedCostOriginal.value.find((r) => Number(r.id) === Number(row.id))
  if (!orig) return true
  return (
    Number(row.qtyPc ?? 0) !== Number(orig.qtyPc ?? 0) ||
    Number(row.qtyKgL ?? 0) !== Number(orig.qtyKgL ?? 0)
  )
}

async function loadSavedCost() {
  savedCostError.value = ''
  savedCostRows.value = []
  savedCostOriginal.value = []
  if (!savedCostDate.value) return
  savedCostLoading.value = true
  try {
    const response = await apiFetch(
      `/debones/cost/saved?date=${encodeURIComponent(savedCostDate.value)}`,
      { method: 'GET' }
    )
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to load saved cost')
    }
    const data = await response.json().catch(() => [])
    const rows = (Array.isArray(data) ? data : []).map((row) => ({
      ...row,
      qtyPc: row.qtyPc ?? '',
      qtyKgL: row.qtyKgL ?? '',
      unitPrice: row.unitPrice ?? 0,
      totalPrice: row.totalPrice ?? 0,
      howManyPiecesUsed: Number(row.howManyPiecesUsed || 0),
      groupingId: row.groupingId || null,
    }))
    savedCostRows.value = rows
    savedCostOriginal.value = rows.map((r) => ({ ...r }))
  } catch (err) {
    console.error(err)
    savedCostError.value = err?.message || 'Failed to load saved cost'
  } finally {
    savedCostLoading.value = false
  }
}

async function handleUpdateSavedCost() {
  savedCostError.value = ''
  if (!canEditDeboneCost.value || !hasSavedCostChanges.value || savedCostUpdating.value) return

  const changedRows = savedCostRows.value.filter(hasSavedCostRowChanged)

  if (changedRows.length === 0) return

  savedCostUpdating.value = true
  try {
    const response = await apiFetch('/debones/cost/saved', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        rows: changedRows.map((row) => ({
          id: row.id,
          qtyPc: row.qtyPc,
          qtyKgL: row.qtyKgL,
          unitPrice: getSavedRowUp(row),
          totalPrice: row.totalPrice,
        })),
      }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(data.message || 'Failed to update saved cost')
    }
    showToast(data.message || 'Saved Cost updated successfully.', 'success')
    await loadSavedCost()
  } catch (err) {
    showToast(err?.message || 'Failed to update saved cost', 'error')
  } finally {
    savedCostUpdating.value = false
  }
}

onMounted(async () => {
  bootstrapLoading.value = true
  try {
    await Promise.all([loadDepartments(), loadRegistrationsForDropdown()])
  } finally {
    bootstrapLoading.value = false
  }
})

onBeforeUnmount(() => {
  if (toastTimer) clearTimeout(toastTimer)
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: collapse;
}

.debone-cost-table {
  table-layout: auto;
  width: 100%;
  min-width: 720px;
}

.debone-cost-table th,
.debone-cost-table td {
  width: auto;
  overflow: hidden;
  text-overflow: ellipsis;
}

th,
td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

th.text-center,
td.text-center {
  text-align: center;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
